import { _x } from '@wordpress/i18n'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useInfiniteHits, useSearchBox, useStats } from 'react-instantsearch'
import FilterPanel from './FilterPanel'
import HitList from './List'
import useScrollDirection from '../../../../assets/frontend/js/hooks/useScrollDirection'
import useBreakpoint from '../../../../assets/frontend/js/hooks/breakpoint'
import Sticky from '../../../../assets/frontend/js/components/Sticky'
import useIsFullscreen from '../../../../assets/frontend/js/hooks/useIsFullscreen'
import ActionButtons from '../../../../assets/frontend/js/components/ActionButtons'
import LayoutToggle from '../../../../assets/frontend/js/components/LayoutToggle'
import { EventsFiltersController } from './EventsFiltersController'
import { getFilterLayoutFlags, useConfigContext } from './ConfigContext'

function SearchUI() {
  const { items, isLastPage, showMore } = useInfiniteHits()
  const { query } = useSearchBox()
  const { nbHits } = useStats()

  const modalIdRef = useRef(useId())

  const timeoutRef = useRef<NodeJS.Timeout>()
  const lastTrackedQuery = useRef<string>('')

  const trackSearch = useCallback(() => {
    if (!window.dataLayer) return
    const cleanQuery = query.trim()
    if (cleanQuery && cleanQuery !== lastTrackedQuery.current) {
      lastTrackedQuery.current = cleanQuery

      window.dataLayer.push({
        event: 'events_search',
        search_query: cleanQuery,
        search_results_count: nbHits,
        search_no_results: nbHits === 0
      })
    }
  }, [query, nbHits])

  useEffect(() => {
    if (query.trim()) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(trackSearch, 1000)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [query, trackSearch])

  const { filterLayout } = useConfigContext()
  const { showSearchPanel: showFilterPanel, showFilterBar: showActionButtons } =
    getFilterLayoutFlags(filterLayout)

  const [layout, setLayout] = useState<'card' | 'list'>('card')

  const isDesktop = useBreakpoint('md')

  const wrapperRef = useRef<HTMLDivElement>(null)
  const isWrapperVisible = useIsFullscreen(wrapperRef)
  const scrollDirection = useScrollDirection()

  const collapseUi =
    !isDesktop && scrollDirection === 'down' && isWrapperVisible

  return (
    <>
      <div className="rounded overflow-clip" ref={wrapperRef}>
        <EventsFiltersController
          startAttribute="startDate"
          endAttribute="endDate"
        >
          {showFilterPanel && (
            <Sticky isSticky={!isDesktop}>
              <FilterPanel
                layout={layout}
                onLayoutChange={setLayout}
                collapsed={collapseUi}
                isDesktop={isDesktop}
                modalId={modalIdRef.current}
              />
            </Sticky>
          )}
        </EventsFiltersController>
        <div className="my-2">
          <HitList items={items} layout={layout} />
          {!isLastPage && (
            <div className="text-center mt-2">
              <button
                type="button"
                className="btn btn-default"
                onClick={showMore}
              >
                {_x('Show more', 'Events List Show More Button', 'icf')}
              </button>
            </div>
          )}
        </div>
        {showActionButtons && (
          <Sticky alignment="bottom">
            <ActionButtons
              collapsed={collapseUi}
              layoutToggle={
                <LayoutToggle
                  variant="mobile"
                  value={layout}
                  onChange={setLayout}
                />
              }
              modalId={modalIdRef.current}
            />
          </Sticky>
        )}
      </div>
    </>
  )
}

export default SearchUI
